import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

const Ctx = createContext(null);
const LS_KEY = "ct_selected_device";

export function DeviceProvider({ children }) {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, _setSelectedDeviceId] = useState(
    () => localStorage.getItem(LS_KEY) || null
  );
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user) {
      setDevices([]);
      setLoading(false);
      return [];
    }
    try {
      const { data } = await api.get("/device/mine");
      setDevices(data);
      // If current selection is gone, fall back to first
      _setSelectedDeviceId((cur) => {
        const stillExists = data.some((d) => d.device_id === cur);
        if (stillExists) return cur;
        const next = data[0]?.device_id || null;
        if (next) localStorage.setItem(LS_KEY, next);
        else localStorage.removeItem(LS_KEY);
        return next;
      });
      return data;
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  // React to 403 errors — device may have been unlinked server-side
  useEffect(() => {
    const handler = () => reload();
    window.addEventListener("ct:device-forbidden", handler);
    return () => window.removeEventListener("ct:device-forbidden", handler);
  }, [reload]);

  const setSelectedDeviceId = (id) => {
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
    _setSelectedDeviceId(id);
  };

  const deleteDevice = async (deviceId) => {
    await api.delete(`/device/${deviceId}`);
    await reload();
  };

  const selectedDevice = devices.find((d) => d.device_id === selectedDeviceId) || null;

  return (
    <Ctx.Provider
      value={{ devices, selectedDeviceId, setSelectedDeviceId, selectedDevice, loading, reload, deleteDevice }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useDevices() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useDevices must be used inside DeviceProvider");
  return v;
}
